import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Dispensaries from "@/pages/Dispensaries";
import DispensaryDetail from "@/pages/DispensaryDetail";
import Settings from "@/pages/Settings";
import Snapshots from "@/pages/Snapshots";
import SnapshotView from "@/pages/SnapshotView";
import MapPage from "@/pages/MapPage";
import BusTastings from "@/pages/BusTastings";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import MyTastings from "@/pages/MyTastings";
import AdminUsers from "@/pages/AdminUsers";
import Recap from "@/pages/Recap";
import Partners from "@/pages/Partners";
import FeedbackPage from "@/pages/FeedbackPage";
import TripDetail from "@/pages/TripDetail";
import ProtectedRoute from "@/components/ProtectedRoute";
import { AuthProvider, useAuth } from "@/lib/AuthProvider";
import { ThemeProvider, useTheme } from "@/lib/ThemeProvider";

/** Redirects "/" to the user's role-specific landing page (or /login). */
function HomeRedirect() {
  const { user, loading, landingPath } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? landingPath : "/login"} replace />;
}

/** Detects the OAuth callback fragment BEFORE normal routing kicks in.
 *  Emergent Auth returns to "/" with #session_id=… in the URL fragment. */
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Public, no-auth anonymous feedback page. Lives outside the
          ProtectedRoute + Layout so QR scanners aren't redirected to
          /login. */}
      <Route path="/feedback/:tastingId" element={<FeedbackPage />} />
      <Route path="/" element={<HomeRedirect />} />

      {/* All authenticated routes share the main Layout (including /my-tastings). */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route
          path="/my-tastings"
          element={
            <ProtectedRoute>
              <MyTastings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dispensaries"
          element={
            <ProtectedRoute allowedRoles={["admin", "dispatcher", "sales"]}>
              <Dispensaries />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dispensaries/:id"
          element={
            <ProtectedRoute allowedRoles={["admin", "dispatcher", "sales"]}>
              <DispensaryDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/map"
          element={
            <ProtectedRoute allowedRoles={["admin", "dispatcher"]}>
              <MapPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-tastings"
          element={
            <ProtectedRoute allowedRoles={["admin", "dispatcher"]}>
              <BusTastings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trips/:id"
          element={
            <ProtectedRoute allowedRoles={["admin", "dispatcher", "driver", "host", "lead_host"]}>
              <TripDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/snapshots"
          element={
            <ProtectedRoute allowedRoles={["admin", "dispatcher"]}>
              <Snapshots />
            </ProtectedRoute>
          }
        />
        <Route
          path="/partners"
          element={
            <ProtectedRoute allowedRoles={["admin", "dispatcher", "sales", "lead_host", "host"]}>
              <Partners />
            </ProtectedRoute>
          }
        />
        <Route
          path="/recap"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Recap />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path="/snapshots/:id"
        element={
          <ProtectedRoute allowedRoles={["admin", "dispatcher"]}>
            <SnapshotView />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

function App() {
  // Theme is now managed by ThemeProvider (reads localStorage, applies
  // either .light or .dark on <html>). No need to force .dark here.
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRouter />
          <ThemedToaster />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

/** Toaster that mirrors the current theme so toasts stay readable in
 *  both palettes. Can't live in <App> directly because it needs the
 *  ThemeProvider context. */
function ThemedToaster() {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  return (
    <Toaster
      theme={isDark ? "dark" : "light"}
      toastOptions={{
        style: isDark
          ? {
              background: "hsl(240 5% 10%)",
              border: "1px solid hsl(240 4% 18%)",
              color: "hsl(0 0% 98%)",
            }
          : {
              background: "#ffffff",
              border: "1px solid #e4e4e7",
              color: "hsl(240 10% 12%)",
            },
      }}
    />
  );
}

export default App;
